import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, RefreshCw, XCircle } from "lucide-react";
import { AdminNav } from "@/components/admin/AdminNav";
import { api } from "@/lib/api";
import { SEO } from "@/lib/seo";
import "@/styles/brand-a.css";
import "@/styles/admin-invites.css";
import "@/styles/board.css";

type MembershipStatus = "new" | "approved" | "approved_manual" | "declined";

interface MembershipRequest {
  id: string;
  role: "gp" | "specialist";
  name: string;
  email: string;
  ahpraNumber: string;
  practiceName: string;
  message: string;
  status: MembershipStatus;
  createdAt: string;
}

interface MembershipResponse {
  items: MembershipRequest[];
  total: number;
}

interface ApproveResponse {
  ok: true;
  status: "approved" | "approved_manual";
  keycloakUserId?: string;
  manualSteps?: string[];
}

export default function Membership() {
  const queryClient = useQueryClient();
  const [declineReasons, setDeclineReasons] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [manualNotice, setManualNotice] = useState<{ name: string; steps: string[] } | null>(null);
  const membership = useQuery({
    queryKey: ["admin-membership", "new"],
    queryFn: () => api.get<MembershipResponse>("/v1/admin/membership", { params: { status: "new" } }),
  });
  const approve = useMutation({
    mutationFn: (request: MembershipRequest) => api.post<ApproveResponse>(`/v1/admin/membership/${request.id}/approve`),
    onSuccess: (response, request) => {
      if (response.status === "approved_manual" && response.manualSteps?.length) {
        setManualNotice({ name: request.name, steps: response.manualSteps });
        setNotice(`${request.name} was approved for manual account creation.`);
      } else {
        setManualNotice(null);
        setNotice(`${request.name} was approved and the account email was triggered.`);
      }
      void queryClient.invalidateQueries({ queryKey: ["admin-membership"] });
    },
  });
  const decline = useMutation({
    mutationFn: ({ request, reason }: { request: MembershipRequest; reason: string }) =>
      api.post<{ ok: true }>(`/v1/admin/membership/${request.id}/decline`, { reason }),
    onSuccess: (_response, { request }) => {
      setNotice(`${request.name}'s request was declined.`);
      void queryClient.invalidateQueries({ queryKey: ["admin-membership"] });
    },
  });

  return (
    <div className="brand-a pb-admin-invites-page pb-admin-membership-page">
      <SEO title="Membership admin · PulseBoard" description="Review PulseBoard clinician membership requests." path="/admin/membership" noindex />
      <main className="pb-admin-invites-main">
        <AdminNav />
        <header className="pb-admin-heading">
          <div><p>Admin · Clinician cockpit</p><h1>Membership queue</h1></div>
          <button type="button" onClick={() => void membership.refetch()}><RefreshCw size={15} aria-hidden="true" />Refresh</button>
        </header>

        {notice ? <p className="pb-admin-membership-notice" role="status">{notice}</p> : null}
        {manualNotice ? (
          <section className="pb-admin-manual-steps" aria-labelledby="manual-steps-heading">
            <h2 id="manual-steps-heading">Manual account steps · {manualNotice.name}</h2>
            <ol>{manualNotice.steps.map((step, index) => <li key={`${index}-${step}`}>{step}</li>)}</ol>
          </section>
        ) : null}
        {approve.isError ? <p className="pb-admin-state pb-admin-state--error" role="alert">Approval failed. The request is unchanged; check Keycloak availability and try again.</p> : null}
        {decline.isError ? <p className="pb-admin-state pb-admin-state--error" role="alert">The request could not be declined. Try again.</p> : null}

        <section aria-labelledby="new-membership-heading">
          <div className="pb-admin-section-heading">
            <div><h2 id="new-membership-heading">New requests</h2><p>AHPRA-number-recorded, identity-checked by PulseBoard on approval.</p></div>
            {membership.data ? <span className="pb-admin-membership-total">{membership.data.total} awaiting review</span> : null}
          </div>
          {membership.isLoading ? <p className="pb-admin-state">Loading membership requests…</p> : null}
          {membership.isError ? <p className="pb-admin-state pb-admin-state--error" role="alert">Membership requests could not be loaded.</p> : null}
          {membership.data?.items.length === 0 ? <p className="pb-admin-state">No new membership requests.</p> : null}
          <div className="pb-admin-membership-list">
            {membership.data?.items.map((request) => {
              const reason = declineReasons[request.id] ?? "";
              const busy = (approve.isPending && approve.variables?.id === request.id) || (decline.isPending && decline.variables?.request.id === request.id);
              return (
                <article key={request.id} className="pb-admin-membership-card">
                  <header>
                    <div><span>{request.role === "gp" ? "GP" : "Specialist"}</span><h3>{request.name}</h3><p>{request.practiceName || "Practice not supplied"}</p></div>
                    <time dateTime={request.createdAt}>{formatDateTime(request.createdAt)}</time>
                  </header>
                  <dl>
                    <div><dt>Email</dt><dd>{request.email}</dd></div>
                    <div><dt>AHPRA number</dt><dd>{request.ahpraNumber}</dd></div>
                    <div><dt>Status</dt><dd>{request.status}</dd></div>
                  </dl>
                  {request.message ? <div className="pb-admin-membership-message"><span>Message</span><p>{request.message}</p></div> : null}
                  <footer>
                    <button type="button" disabled={busy} onClick={() => { setNotice(null); setManualNotice(null); approve.mutate(request); }}><CheckCircle2 size={15} aria-hidden="true" />{busy && approve.variables?.id === request.id ? "Approving…" : "Approve"}</button>
                    <label>Decline reason<input maxLength={300} value={reason} onChange={(event) => setDeclineReasons((current) => ({ ...current, [request.id]: event.target.value }))} /></label>
                    <button type="button" className="pb-admin-danger" disabled={busy || !reason.trim()} onClick={() => { setNotice(null); decline.mutate({ request, reason: reason.trim() }); }}><XCircle size={15} aria-hidden="true" />Decline</button>
                  </footer>
                </article>
              );
            })}
          </div>
        </section>
      </main>
    </div>
  );
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat("en-AU", { dateStyle: "medium", timeStyle: "short" }).format(date);
}
